import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Tippy from "@tippyjs/react";
import { parseChildrenBySlots } from "UTILS";
import { Icon } from "../Icon";
import { Section, SectionTheme, SectionPadding } from "../Section";
import { Okta } from "./Okta/Okta";
import { Menu } from "./Menu/Menu";
import styles from "./Header.module.scss";

const defaultLabels = {
	oktaOpenMenu: "Open account profile menu",
	oktaCloseMenu: "Close account profile menu",
	oktaToggleDropdown: "Toggle account profile dropdown menu",
	oktaMobileMenuTitle: "Account Profile",
	oktaWelcome: "Welcome",
	menu: "Menu",
	openMenu: "Open menu",
	closeMenu: "Close menu",
	openGatewayMenu: "Open gateway menu",
	closeGatewayMenu: "Close gateway menu",
	changeGateway: "Change gateway",
	gatewayHeader: "Gateway",
};

const defaultAlerts = {
	count: 0,
	label: "Travel advisories",
	target: "_self",
	link: "/travel-alerts",
	renderButton: (link, target, className, children) => (
		<a href={link} target={target} className={className}>
			{children}
		</a>
	),
};

const Header = ({
	classNames,
	labels: providedLabels,
	logo,
	logoOnly,
	renderLink,
	links,
	alerts: providedAlerts,
	renderMenuLink,
	menuLinks,
	profileData,
	currentLanguage,
	languagePages,
	showGateway,
	onGatewayChange,
	handleChangeLanguage,
	focusOnShouldIgnore,
	children,
}) => {
	const labels = { ...defaultLabels, ...providedLabels };
	const alerts = { ...defaultAlerts, ...providedAlerts };

	const slots = parseChildrenBySlots(children);

	return (
		<header className={cx(classNames.header)}>
			{slots.get("header--start")}
			<Section className={styles.header} theme={SectionTheme.none} padding={SectionPadding.none}>
				{slots.get("header--cta-message", slot => (
					<div className={styles.headerCtaMessageWrapper}>{slot}</div>
				))}
				<div className={styles.headerWrapper}>
					<div className={styles.headerImage}>{logo}</div>
					{!logoOnly && (
						<>
							{!!links?.length && (
								<nav className={styles.headerNav}>
									<ul className={styles.headerNavItems}>
										{links.map(link => (
											<li key={link?.linkText}>
												{renderLink(link, {
													classNames: {
														link: styles.headerNavLink,
														active: styles.headerNavLinkActive,
													},
												})}
											</li>
										))}
									</ul>
								</nav>
							)}
							<div className={styles.headerSecondaryNav} role="menu" aria-label="More menu options">
								{slots.get("secondary-nav--start")}
								{alerts.count >= 1 && (
									<Tippy
										role="tooltip"
										placement="bottom"
										content={<div className={styles.tooltip}>{alerts.label}</div>}>
										{alerts.renderButton(
											alerts.link,
											alerts.target,
											styles.alertBtnInfo,
											<>
												<span className={styles.alertBtnNotification}>{alerts.count}</span>
												<span className={styles.alertBtnInfoWrapper}>
													<Icon name="bell" />
												</span>
											</>
										)}
									</Tippy>
								)}
								{slots.get("secondary-nav--after-alerts")}
								{slots.get("okta", slot => (
									<Okta
										labels={{
											openMenu: labels.oktaOpenMenu,
											closeMenu: labels.oktaCloseMenu,
											toggleDropdown: labels.oktaToggleDropdown,
											mobileMenuTitle: labels.oktaMobileMenuTitle,
											welcome: labels.oktaWelcome,
										}}
										profileData={profileData}
										focusOnShouldIgnore={focusOnShouldIgnore}>
										{slot}
									</Okta>
								))}
								{slots.get("secondary-nav--end")}
								{menuLinks && (
									<Menu
										labels={{
											menu: labels.menu,
											openMenu: labels.openMenu,
											closeMenu: labels.closeMenu,
											openGatewayMenu: labels.openGatewayMenu,
											closeGatewayMenu: labels.closeGatewayMenu,
											changeGateway: labels.changeGateway,
											gatewayHeader: labels.gatewayHeader,
										}}
										renderMenuLink={renderMenuLink}
										menuLinks={menuLinks}
										currentLanguage={currentLanguage}
										languagePages={languagePages}
										showGateway={showGateway}
										onGatewayChange={onGatewayChange}
										handleChangeLanguage={handleChangeLanguage}
										focusOnShouldIgnore={focusOnShouldIgnore}
									/>
								)}
							</div>
						</>
					)}
				</div>
			</Section>
			{slots.get("widget", slot => (
				<Section padding="none" className={styles.widget}>
					{slot}
				</Section>
			))}
		</header>
	);
};

Header.propTypes = {
	classNames: PropTypes.shape({
		header: PropTypes.string,
	}),
	labels: PropTypes.shape({
		oktaOpenMenu: PropTypes.string,
		oktaCloseMenu: PropTypes.string,
		oktaToggleDropdown: PropTypes.string,
		oktaMobileMenuTitle: PropTypes.string,
		oktaWelcome: PropTypes.string,
		menu: PropTypes.string,
		openMenu: PropTypes.string,
		closeMenu: PropTypes.string,
		openGatewayMenu: PropTypes.string,
		closeGatewayMenu: PropTypes.string,
		changeGateway: PropTypes.string,
		gatewayHeader: PropTypes.string,
	}),
	logo: PropTypes.node,
	logoOnly: PropTypes.bool,
	renderLink: PropTypes.func,
	links: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			linkText: PropTypes.string.isRequired,
			target: PropTypes.string,
			url: PropTypes.string.isRequired,
		})
	),
	alerts: PropTypes.shape({
		count: PropTypes.number,
		label: PropTypes.node,
		target: PropTypes.string,
		link: PropTypes.string,
		renderButton: PropTypes.func,
	}),
	renderMenuLink: PropTypes.func,
	menuLinks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			links: PropTypes.arrayOf(
				PropTypes.shape({
					displayTitle: PropTypes.string,
					links: PropTypes.arrayOf(
						PropTypes.shape({
							id: PropTypes.string,
							linkText: PropTypes.string,
							url: PropTypes.string,
							target: PropTypes.string,
						})
					),
					id: PropTypes.string,
					linkText: PropTypes.string,
					url: PropTypes.string,
					target: PropTypes.string,
				})
			),
		})
	),
	profileData: PropTypes.shape({
		isUserLoggedIn: PropTypes.bool,
		userName: PropTypes.string,
	}),
	currentLanguage: PropTypes.string.isRequired,
	languagePages: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			lang: PropTypes.string.isRequired,
			path: PropTypes.string.isRequired,
		}).isRequired
	).isRequired,
	showGateway: PropTypes.bool,
	onGatewayChange: PropTypes.func,
	handleChangeLanguage: PropTypes.func,
	focusOnShouldIgnore: PropTypes.func,
	/**
	 * `slot` (header--start | header--cta-message | secondary-nav--start | secondary-nav--after-alerts | okta | secondary-nav--end | widget) is required as an attribute for each child
	 */
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

Header.defaultProps = {
	classNames: {},
	labels: defaultLabels,
	logo: (
		<img
			src="https://assets.sunwingtravelgroup.com/image/upload/v1614627905/sunwing-prod/Web%20Replatform%20Project/Logos/SunwingLogos/NewSunwingLogo-white.svg"
			alt="Sunwing logo"
		/>
	),
	logoOnly: false,
	renderLink: (link, { classNames }) => (
		<a href={link.url} target={link.target} className={classNames.link}>
			{link.linkText}
		</a>
	),
	links: [],
	profileData: {
		isUserLoggedIn: false,
		userName: undefined,
	},
	alerts: defaultAlerts,
	renderMenuLink: (link, { classNames }) => (
		<a href={link.url} target={link.target} className={classNames?.link}>
			{link.linkText}
		</a>
	),
	menuLinks: [],
	showGateway: true,
	onGatewayChange: () => {},
	handleChangeLanguage: () => {},
	focusOnShouldIgnore: () => true,
	children: undefined,
};

export default Header;
export { Header };
